import type { AdminFinanceStats } from '../../../domain/superadmin/types';
import { formatCurrency } from '../../../utils/formatters';
import DashboardCard from '../../dashboard/components/DashboardCard';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';

type RevenueByPlanSectionProps = {
  rows: AdminFinanceStats['revenueByPlan'];
};

export function RevenueByPlanSection({ rows }: RevenueByPlanSectionProps) {
  const totals = rows.reduce(
    (acc, row) => ({
      clients: acc.clients + row.clients,
      baseMrrCents: acc.baseMrrCents + row.baseMrrCents,
      addonsMrrCents: acc.addonsMrrCents + row.addonsMrrCents,
      totalMrrCents: acc.totalMrrCents + row.totalMrrCents,
    }),
    { clients: 0, baseMrrCents: 0, addonsMrrCents: 0, totalMrrCents: 0 },
  );

  return (
    <section aria-label="Receita por plano" className="mb-s5">
      <DashboardCard title="Receita por plano">
        <p className="mb-s4 text-[12px] text-muted">
          Assinaturas ativas · MRR base e add-ons por plano
        </p>
        {rows.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border bg-surface px-s4 py-6 text-center text-[13px] text-muted">
            Nenhum plano com assinaturas ativas.
          </p>
        ) : (
          <>
            <ul className="m-0 flex list-none flex-col gap-3 p-0 md:hidden">
              {rows.map((row) => (
                <li
                  key={row.planId}
                  className="rounded-lg border border-subtle bg-surface px-s4 py-3"
                >
                  <p className="font-medium text-default">{row.planLabel}</p>
                  <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-muted">
                    <dt>Clientes</dt>
                    <dd className="text-right font-mono tabular-nums text-secondary">{row.clients}</dd>
                    <dt>MRR base</dt>
                    <dd className="text-right font-mono tabular-nums text-secondary">
                      {formatCurrency(row.baseMrrCents / 100, 'BRL')}
                    </dd>
                    <dt>Add-ons</dt>
                    <dd className="text-right font-mono tabular-nums text-secondary">
                      {formatCurrency(row.addonsMrrCents / 100, 'BRL')}
                    </dd>
                    <dt>Total MRR</dt>
                    <dd className="text-right font-mono font-medium tabular-nums text-default">
                      {formatCurrency(row.totalMrrCents / 100, 'BRL')}
                    </dd>
                  </dl>
                </li>
              ))}
              <li className="rounded-lg border border-subtle bg-surface-subtle px-s4 py-3">
                <p className="font-semibold text-default">Total</p>
                <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-muted">
                  <dt>Clientes</dt>
                  <dd className="text-right font-mono font-semibold tabular-nums text-default">
                    {totals.clients}
                  </dd>
                  <dt>Total MRR</dt>
                  <dd className="text-right font-mono font-semibold tabular-nums text-default">
                    {formatCurrency(totals.totalMrrCents / 100, 'BRL')}
                  </dd>
                </dl>
              </li>
            </ul>

            <div className="hidden overflow-x-auto md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Plano</TableHead>
                  <TableHead className="text-right">Clientes</TableHead>
                  <TableHead className="text-right">MRR base</TableHead>
                  <TableHead className="text-right">Add-ons</TableHead>
                  <TableHead className="text-right">Total MRR</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.planId}>
                    <TableCell className="font-medium text-default">{row.planLabel}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums">{row.clients}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {formatCurrency(row.baseMrrCents / 100, 'BRL')}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {formatCurrency(row.addonsMrrCents / 100, 'BRL')}
                    </TableCell>
                    <TableCell className="text-right font-mono font-medium tabular-nums text-default">
                      {formatCurrency(row.totalMrrCents / 100, 'BRL')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell className="font-semibold text-default">Total</TableCell>
                  <TableCell className="text-right font-mono font-semibold tabular-nums">
                    {totals.clients}
                  </TableCell>
                  <TableCell className="text-right font-mono font-semibold tabular-nums">
                    {formatCurrency(totals.baseMrrCents / 100, 'BRL')}
                  </TableCell>
                  <TableCell className="text-right font-mono font-semibold tabular-nums">
                    {formatCurrency(totals.addonsMrrCents / 100, 'BRL')}
                  </TableCell>
                  <TableCell className="text-right font-mono font-semibold tabular-nums text-default">
                    {formatCurrency(totals.totalMrrCents / 100, 'BRL')}
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
            </div>
          </>
        )}
      </DashboardCard>
    </section>
  );
}
